// AdvancetableFootervendorProduct.tsx
import { useEffect } from 'react';
import { Col, Pagination, Row } from 'react-bootstrap';
import Button from './Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faAngleRight,
  faChevronLeft,
  faChevronRight
} from '@fortawesome/free-solid-svg-icons';
import classNames from 'classnames';
import usePagination from 'hooks/usePagination';

interface Props {
  className?: string;
  pagination?: boolean;
  page: number;
  setPage: (p: number) => void;
  limit: number;
  setLimit: (l: number) => void;
  total: number;
}

const AdvancetableFootervendorProduct = ({
  className,
  pagination,
  page,
  setPage,
  limit,
  setLimit,
  total
}: Props) => {
  const totalPage = Math.ceil(total / limit);

  const { hasNextEllipsis, hasPrevEllipsis, visiblePaginationItems } =
    usePagination({
      currentPageNo: page,
      totalPage,
      maxPaginationButtonCount: 5
    });

  useEffect(() => {
    if (page > totalPage && totalPage > 0) setPage(1);
  }, [totalPage]);

  return (
    <Row className={classNames(className, 'align-items-center py-1')}>
      <Col className="d-flex fs-9">
        <p className="mb-0 d-none d-sm-block me-3 fw-semibold text-body">
          {total === 0 ? 0 : (page - 1) * limit + 1} to{' '}
          {Math.min(page * limit, total)}{' '}
          <span className="text-body-tertiary">items of</span> {total}
        </p>
        <Button
          variant="link"
          className="p-0 fw-semibold"
          endIcon={
            <FontAwesomeIcon icon={faAngleRight} className="ms-1 fs-9" />
          }
          onClick={() => setLimit(limit === 15 ? 100 : 15)}
        >
          View {limit === 15 ? 'all' : 'less'}
        </Button>
      </Col>

      {pagination && totalPage > 1 && (
        <Col xs="auto">
          <Pagination className="mb-0 justify-content-center">
            <Pagination.Prev
              disabled={page <= 1}
              onClick={() => setPage(page - 1)}
            >
              <FontAwesomeIcon icon={faChevronLeft} />
            </Pagination.Prev>

            {hasPrevEllipsis && (
              <>
                <Pagination.Item active={page === 1} onClick={() => setPage(1)}>
                  1
                </Pagination.Item>
                <Pagination.Ellipsis disabled />
              </>
            )}

            {visiblePaginationItems.map(p => (
              <Pagination.Item
                key={p}
                active={page === p}
                onClick={() => setPage(p)}
              >
                {p}
              </Pagination.Item>
            ))}

            {hasNextEllipsis && (
              <>
                <Pagination.Ellipsis disabled />
                <Pagination.Item
                  active={page === totalPage}
                  onClick={() => setPage(totalPage)}
                >
                  {totalPage}
                </Pagination.Item>
              </>
            )}

            <Pagination.Next
              disabled={page >= totalPage}
              onClick={() => setPage(page + 1)}
            >
              <FontAwesomeIcon icon={faChevronRight} />
            </Pagination.Next>
          </Pagination>
        </Col>
      )}
    </Row>
  );
};

export default AdvancetableFootervendorProduct;
